import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonProps } from '@sorare/core/src/atoms/buttons/ButtonBase';
import LoadingButton from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import Radio from '@sorare/core/src/atoms/inputs/Radio';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelM } from '@sorare/core/src/atoms/typography';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { rivals } from '@sorare/core/src/lib/glossary';

import { VisibilityLabel } from 'components/rivals/squads/InviteDialog/VisibilityLabel';

import {
  UpdateSquadVisibilityMutation,
  UpdateSquadVisibilityMutationVariables,
  VisibilitySettings_userGroupInterface,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
`;

const UPDATE_SQUAD_VISIBILITY_MUTATION = gql`
  mutation UpdateSquadVisibilityMutation($input: updateSo5UserGroupInput!) {
    updateSo5UserGroup(input: $input) {
      userGroup {
        slug
        publiclyAccessible
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdateSquadVisibilityMutation,
  UpdateSquadVisibilityMutationVariables
>;

type Props = {
  userGroup: VisibilitySettings_userGroupInterface;
  renderButton: (
    props: Pick<ButtonProps, 'onClick' | 'onMouseEnter' | 'onMouseLeave'> & {
      publiclyAccessible: boolean;
    }
  ) => ReactElement;
};
export const VisibilitySettings = ({ userGroup, renderButton }: Props) => {
  const [publiclyAccessible, setPubliclyAccessible] = useState(
    userGroup.publiclyAccessible
  );
  const [ctaPubliclyAccessible, setCtaPubliclyAccessible] = useState(
    userGroup.publiclyAccessible
  );
  const [update, { loading }] = useMutation(UPDATE_SQUAD_VISIBILITY_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  return (
    <Dropdown
      label={props =>
        renderButton({ publiclyAccessible: ctaPubliclyAccessible, ...props })
      }
      gap={8}
    >
      {({ closeDropdown }) => (
        <Root>
          <Vertical gap={2}>
            <LabelM color="var(--c-neutral-700)">
              <FormattedMessage {...rivals.squadVisibility} />
            </LabelM>
            <Radio
              name="public"
              value={publiclyAccessible.toString()}
              checked={publiclyAccessible}
              onChange={() => {
                setPubliclyAccessible(true);
              }}
              labelContent={
                <Vertical gap={0.5}>
                  <VisibilityLabel publiclyAccessible />
                  <BodyS color="var(--c-neutral-700)">
                    <FormattedMessage
                      id="VisibilitySettings.publicDescription"
                      defaultMessage="Anyone can join your squad"
                    />
                  </BodyS>
                </Vertical>
              }
            />

            <Radio
              name="private"
              value={publiclyAccessible.toString()}
              checked={!publiclyAccessible}
              onChange={() => {
                setPubliclyAccessible(false);
              }}
              labelContent={
                <Vertical gap={0.5}>
                  <VisibilityLabel publiclyAccessible={false} />
                  <BodyS color="var(--c-neutral-700)">
                    <FormattedMessage
                      id="VisibilitySettings.privateDescription"
                      defaultMessage="Only friends with the code can join"
                    />
                  </BodyS>
                </Vertical>
              }
            />
          </Vertical>
          <LoadingButton
            color="secondary"
            size="medium"
            loading={loading}
            onClick={async () => {
              if (publiclyAccessible !== userGroup.publiclyAccessible) {
                await update({
                  variables: {
                    input: {
                      so5UserGroupId: userGroup.id,
                      publiclyAccessible,
                    },
                  },
                });
              }
              setCtaPubliclyAccessible(publiclyAccessible);
              closeDropdown();
            }}
          >
            <FormattedMessage
              id="VisibilitySettings.saveChanges"
              defaultMessage="Save Changes"
            />
          </LoadingButton>
        </Root>
      )}
    </Dropdown>
  );
};

VisibilitySettings.fragments = {
  userGroupInterface: gql`
    fragment VisibilitySettings_userGroupInterface on UserGroupInterface {
      slug
      id
      publiclyAccessible
    }
  ` as TypedDocumentNode<VisibilitySettings_userGroupInterface>,
};
